<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContacts: {
    type: Array,
    default: () => [],
  },
  allowMultiple: {
    type: Boolean,
    default: false,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'addContact',
  'removeContact',
  'updateDropdown',
  'clearSearch',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.EMAIL);

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: email ? `${name} (${email})` : name,
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

const getContactLabel = contact => {
  const { name, email = '', phoneNumber = '' } = contact || {};
  if (email) {
    return `${name} (${email})`;
  }
  if (phoneNumber) {
    return `${name} (${phoneNumber})`;
  }
  return name || '';
};

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

const hasContacts = computed(() => props.selectedContacts.length > 0);

const showTagInput = computed(
  () => !props.isCreatingContact && (!hasContacts.value || props.allowMultiple)
);

const handleInput = value => {
  const trimmed = (value || '').trim();
  inputType.value = trimmed.startsWith('+')
    ? INPUT_TYPES.TEL
    : INPUT_TYPES.EMAIL;

  if (trimmed.length >= 3) {
    emit('searchContacts', trimmed);
  } else {
    emit('clearSearch');
  }
};
</script>

<template>
  <div class="relative flex-1 px-4 py-2 overflow-y-visible">
    <div class="flex items-baseline w-full gap-3 min-h-7 flex-wrap">
      <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="props.isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>

      <template
        v-for="(contact, index) in selectedContacts"
        :key="contact.id || contact.email"
      >
        <div
          class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
          :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ getContactLabel(contact) }}
          </span>
          <Button
            v-if="!contactId"
            variant="ghost"
            icon="i-lucide-x"
            color="slate"
            :disabled="!!contactId"
            size="xs"
            @click="emit('removeContact', index)"
          />
        </div>
      </template>

      <TagInput
        v-if="showTagInput"
        :key="selectedContacts.length"
        :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
        mode="single"
        :menu-items="contactsList"
        :show-dropdown="showContactsDropdown"
        :is-loading="isLoading"
        :disabled="
          !allowMultiple &&
          contactableInboxesList?.length > 0 &&
          showInboxesDropdown
        "
        allow-create
        auto-select-exact-match
        :type="inputType"
        class="flex-1 min-h-7"
        :class="errorClass"
        :focus-on-mount="selectedContacts.length === 0"
        @input="handleInput"
        @on-click-outside="emit('updateDropdown', 'contacts', false)"
        @add="emit('addContact', $event)"
        @remove="emit('removeContact', selectedContacts.length - 1)"
      />
    </div>
  </div>
</template>
